import React, { useState, useEffect } from 'react';
import { Calendar, Badge, Modal, Table, Card, Button, Space, Input, Popconfirm, message, Tag, Tabs, Radio } from 'antd';
import { CalendarOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { examService } from '../../services/examService';
import locale from 'antd/es/calendar/locale/zh_CN';
import dayjs from 'dayjs';
import StudentDetail from '../../pages/Students/components/StudentDetail';
import AV from 'leancloud-storage';
import { handleSubmitResults, handleEditSubmit } from './ExamSummary';
const { TextArea } = Input;
const { confirm } = Modal;

const ExamHistory = () => {
  const [examDates, setExamDates] = useState({});
  const [selectedExam, setSelectedExam] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [studentDetailVisible, setStudentDetailVisible] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [completeStudentData, setCompleteStudentData] = useState({});
  const [isGradingModalVisible, setIsGradingModalVisible] = useState(false);
  const [results, setResults] = useState({});
  const [editingStudent, setEditingStudent] = useState(null);
  const [editResult, setEditResult] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  useEffect(() => {
    fetchExamDates();
  }, []);

  const fetchExamDates = async () => {
    try {
      setLoading(true);
      const exams = await examService.getAllExams();
      
      // Group exams by date
      const dateMap = {};
      exams.forEach(exam => {
        const date = dayjs(exam.date).format('YYYY-MM-DD');
        if (!dateMap[date]) {
          dateMap[date] = [];
        }
        dateMap[date].push(exam);
      });
      
      setExamDates(dateMap);
    } catch (error) {
      console.error('Fetch exam dates error:', error);
      message.error('获取考试记录失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchExamDetails = async (exam) => {
    try {
      setLoading(true);
      const studentDataMap = {};
      
      // Fetch complete data for all students
      const studentPromises = exam.students.map(async (student) => {
        const query = new AV.Query('Student');
        query.include('receiptPic');
        query.include('tuitionReceipt');
        query.include('withdrawReceipt');
        const fullStudent = await query.get(student.studentId);
        
        const completeData = {
          objectId: fullStudent.id,
          fileId: fullStudent.get('fileId'),
          name: fullStudent.get('name'),
          uniqueId: fullStudent.get('uniqueId'),
          phone: fullStudent.get('phone'),
          signUpDate: fullStudent.get('signUpDate'),
          VIP: fullStudent.get('VIP'),
          address: fullStudent.get('address'),
          tuitionFee: fullStudent.get('tuitionFee'),
          referrer: fullStudent.get('referrer'),
          notes: fullStudent.get('notes'),
          ethnicity: fullStudent.get('ethnicity'),
          birthday: fullStudent.get('birthday'),
          local: fullStudent.get('local'),
          verified: fullStudent.get('verified'),
          documented: fullStudent.get('documented'),
          interviewed: fullStudent.get('interviewed'),
          status: fullStudent.get('status'),
          transferred: fullStudent.get('transferred'),
          paid: fullStudent.get('paid'),
          paymentMethod: fullStudent.get('paymentMethod'),
          receiptNumber: fullStudent.get('receiptNumber'),
          gender: fullStudent.get('gender'),
          exams: fullStudent.get('exams'),
          exam1: fullStudent.get('exam1'),
          exam2: fullStudent.get('exam2'),
          exam3: fullStudent.get('exam3'),
          exam4: fullStudent.get('exam4'),
          studyProgress: fullStudent.get('studyProgress'),
          // Add exam booking specific status
          examStatus: student.status || 'pending'
        };
        
        studentDataMap[student.studentId] = completeData;
        return completeData;
      });

      await Promise.all(studentPromises);
      setCompleteStudentData(studentDataMap);
      
    } catch (error) {
      console.error('Fetch complete student data error:', error);
      message.error('获取学员详细信息失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    const exams = examDates[dateStr] || [];
    if (exams.length > 0) {
      setSelectedExam(exams[0]);
      fetchExamDetails(exams[0]);
      setIsModalVisible(true);
    }
    setCurrentDate(date);
  };

  const handleRemoveStudent = async (student) => {
    setCurrentStudent(student);
    setAddingNote(true);
  };

  const handleNoteSubmit = async () => {
    if (!noteContent.trim()) {
      message.error('请输入备注内容');
      return;
    }

    try {
      setLoading(true);
      await examService.removeStudentFromExam(
        selectedExam.objectId,
        currentStudent.objectId,
        {
          content: noteContent,
          date: new Date().toISOString()
        }
      );
      
      // Refresh exam data
      const updatedExam = await examService.getExamById(selectedExam.objectId);
      setSelectedExam(updatedExam);
      
      message.success('已移除学员并添加备注');
      setNoteContent('');
      setAddingNote(false);
      setCurrentStudent(null);
      
      // Refresh calendar data
      fetchExamDates();
    } catch (error) {
      console.error('Remove student error:', error);
      message.error('操作失败');
    } finally {
      setLoading(false);
    }
  };

  const cellRender = (date, info) => {
    if (info.type !== 'date') return null;
    
    const dateStr = date.format('YYYY-MM-DD');
    const exams = examDates[dateStr] || [];

    return exams.map((exam, index) => {
      const totalStudents = exam.students?.length || 0;

      return (
        <Badge 
          key={index}
          status="processing"
          text={
            <span style={{ cursor: 'pointer' }} onClick={() => {
              setSelectedExam(exam);
              setIsModalVisible(true);
            }}>
              {`${exam.examType} ${totalStudents}人 考官: ${exam.examiner || '未指定'}`}
            </span>
          }
        />
      );
    });
  };

  const showStudentDetails = async (student) => {
    console.log('Fetching student details for:', student);
    try {
      // Fetch complete student data using studentId
      const query = new AV.Query('Student');
      query.include('receiptPic');
      query.include('tuitionReceipt');
      query.include('withdrawReceipt');
      const fullStudent = await query.get(student.studentId);
      
      const completeStudentData = {
        objectId: fullStudent.id,
        fileId: fullStudent.get('fileId'),
        name: fullStudent.get('name'),
        uniqueId: fullStudent.get('uniqueId'),
        phone: fullStudent.get('phone'),
        signUpDate: fullStudent.get('signUpDate'),
        VIP: fullStudent.get('VIP'),
        address: fullStudent.get('address'),
        tuitionFee: fullStudent.get('tuitionFee'),
        referrer: fullStudent.get('referrer'),
        notes: fullStudent.get('notes'),
        ethnicity: fullStudent.get('ethnicity'),
        birthday: fullStudent.get('birthday'),
        local: fullStudent.get('local'),
        verified: fullStudent.get('verified'),
        documented: fullStudent.get('documented'),
        interviewed: fullStudent.get('interviewed'),
        status: fullStudent.get('status'),
        transferred: fullStudent.get('transferred'),
        paid: fullStudent.get('paid'),
        paymentMethod: fullStudent.get('paymentMethod'),
        receiptNumber: fullStudent.get('receiptNumber'),
        receiptPic: fullStudent.get('receiptPic'),
        tuitionReceipt: fullStudent.get('tuitionReceipt'),
        gender: fullStudent.get('gender'),
        exams: fullStudent.get('exams'),
        exam1: fullStudent.get('exam1'),
        exam2: fullStudent.get('exam2'),
        exam3: fullStudent.get('exam3'),
        exam4: fullStudent.get('exam4'),
        studyProgress: fullStudent.get('studyProgress'),
        withdrawDate: fullStudent.get('withdrawDate'),
        withdrawReason: fullStudent.get('withdrawReason'),
        withdrawReceipt: fullStudent.get('withdrawReceipt')
      };

      console.log('Complete student data:', completeStudentData);
      setSelectedStudent(completeStudentData);
      setStudentDetailVisible(true);
    } catch (error) {
      console.error('Error fetching student details:', error);
      message.error('获取学员详情失败');
    }
  };

  const columns = [
    {
      title: '档案号',
      dataIndex: 'fileId',
      key: 'fileId',
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => showStudentDetails(record)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      )
    },
    {
      title: '身份证号',
      dataIndex: 'uniqueId',
      key: 'uniqueId',
    },
    {
      title: '电话',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '考试状态',
      key: 'examStatus',
      render: (_, record) => record.status || '待考试'
    },
    {
      title: '备注记录',
      key: 'notes',
      render: (_, record) => (
        <div>
          {record.notes?.map((note, index) => (
            <div key={index} style={{ marginBottom: 4 }}>
              <small style={{ color: '#888' }}>
                {dayjs(note.date).format('YYYY-MM-DD HH:mm')}:
              </small>
              <br />
              {note.content}
            </div>
          ))}
        </div>
      )
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Popconfirm
          title="确定要移除该学员吗？"
          onConfirm={() => handleRemoveStudent(record)}
        >
          <Button type="link" danger>移除</Button>
        </Popconfirm>
      ),
    },
  ];

  const handleTodayClick = () => {
    const today = dayjs();
    setCurrentDate(today);
    handleDateSelect(today);
  };

  const handleExport = () => {
    if (!selectedExam || !completeStudentData) {
      message.error('没有可导出的数据');
      return;
    }

    const completeStudentList = selectedExam.students
      .map(student => completeStudentData[student.studentId])
      .filter(Boolean);  // Remove any undefined entries

    examService.exportBookingToExcel(completeStudentList, selectedExam);
  };

  const renderStudentTable = (status) => {
    if (!selectedExam) return null;

    const filteredStudents = selectedExam.students.filter(student => 
      status === 'pending' ? student.status === 'pending' : student.status !== 'pending'
    );

    const gradingColumns = [
      {
        title: '档案号',
        dataIndex: 'fileId',
        key: 'fileId',
      },
      {
        title: '姓名',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: '身份证号',
        dataIndex: 'uniqueId',
        key: 'uniqueId',
      },
      {
        title: '考试结果',
        key: 'result',
        render: (_, student) => {
          if (status !== 'pending') {
            const statusMap = {
              passed: { text: '通过', color: 'success' },
              failed: { text: '未通过', color: 'error' },
              absent: { text: '缺考', color: 'default' },
              pending: { text: '待考试', color: 'default' }  // Add default status
            };
            // Use student.status or student.result, fallback to 'pending'
            const currentStatus = statusMap[student.status || student.result] || statusMap.pending;
            return <Tag color={currentStatus.color}>{currentStatus.text}</Tag>;
          }
          return (
            <Radio.Group
              value={results[student.studentId]}
              onChange={e => handleResultChange(student.studentId, e.target.value)}
            >
              <Space>
                <Radio.Button value="passed">通过</Radio.Button>
                <Radio.Button value="failed">不通过</Radio.Button>
                <Radio.Button value="absent">缺考</Radio.Button>
              </Space>
            </Radio.Group>
          );
        }
      },
      {
        title: '操作',
        key: 'action',
        render: (_, student) => 
          status !== 'pending' && (
            <Button 
              type="link" 
              onClick={() => handleEditGrade(student)}
            >
              修改
            </Button>
          )
      }
    ];

    return (
      <Table
        columns={gradingColumns}
        dataSource={filteredStudents}
        rowKey="studentId"
        pagination={{
          pageSize: 10,
          showTotal: (total) => `共 ${total} 名学员`,
          showSizeChanger: true,
          showQuickJumper: true,
        }}
      />
    );
  };

  const handleResultChange = (studentId, value) => {
    setResults(prev => ({
      ...prev,
      [studentId]: value
    }));
  };

  const handleEditGrade = (student) => {
    setEditingStudent(student);
    setEditResult(student.result);
    setIsEditModalVisible(true);
  };

  const handleShowGrading = () => {
    setIsGradingModalVisible(true);
  };

  const handleCancelExam = async (exam) => {
    confirm({
      title: '确认取消考试预约？',
      icon: <ExclamationCircleOutlined />,
      content: `确定要取消 ${exam.examType} (${dayjs(exam.date).format('YYYY-MM-DD')}) 的考试预约吗？`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          setLoading(true);
          await examService.cancelExam(exam.objectId);
          message.success('考试预约已取消');
          await fetchExamDates(); // Refresh the calendar
        } catch (error) {
          message.error(error.message || '取消考试预约失败');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const renderExamDetails = (exam) => {
    const examDate = dayjs(exam.date);
    const canCancel = examDate.isAfter(dayjs(), 'day'); // Only allow cancellation for future exams

    return (
      <div>
        {/* ... existing exam details ... */}
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            {/* ... existing buttons ... */}
            {canCancel && (
              <Button 
                danger
                onClick={() => handleCancelExam(exam)}
                disabled={loading}
              >
                取消考试
              </Button>
            )}
          </Space>
        </div>
      </div>
    );
  };

  return (
    <>
      <Card 
        title="考试记录"
        extra={
          <Button 
            icon={<CalendarOutlined />} 
            onClick={handleTodayClick}
          >
            回到今天
          </Button>
        }
      >
        <Calendar 
          locale={locale}
          cellRender={cellRender}
          onSelect={handleDateSelect}
          value={currentDate}
          onChange={(date) => setCurrentDate(date)}
        />

        <Modal
          title={selectedExam ? `${dayjs(selectedExam.date.iso || selectedExam.date).format('YYYY-MM-DD')} ${selectedExam.examType}考试详情` : ''}
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={[
            <Button 
              key="grade" 
              type="primary"
              onClick={handleShowGrading}
            >
              录入成绩
            </Button>,
            <Button 
              key="export" 
              onClick={handleExport}
              loading={loading}
            >
              导出名单
            </Button>,
            <Button 
              key="close" 
              onClick={() => setIsModalVisible(false)}
            >
              关闭
            </Button>
          ]}
          width="80%"
        >
          {selectedExam && (
            <>
              <div style={{ marginBottom: 16 }}>
                <p>考官：{selectedExam.examiner}</p>
                <p>状态：{selectedExam.status}</p>
                <p>创建时间：{dayjs(selectedExam.createdAt).format('YYYY-MM-DD HH:mm')}</p>
                <p>创建人：{selectedExam.createdBy}</p>
              </div>
              <Table
                columns={columns}
                dataSource={selectedExam.students}
                rowKey="studentId"
                pagination={{
                  pageSize: 10,
                  showTotal: (total) => `共 ${total} 名学员`,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                }}
              />
            </>
          )}
        </Modal>

        <Modal
          title="添加移除原因"
          open={addingNote}
          onOk={handleNoteSubmit}
          onCancel={() => {
            setAddingNote(false);
            setNoteContent('');
            setCurrentStudent(null);
          }}
          confirmLoading={loading}
        >
          <TextArea
            rows={4}
            value={noteContent}
            onChange={e => setNoteContent(e.target.value)}
            placeholder="请输入移除原因"
          />
        </Modal>
      </Card>

      <Modal
        title={`成绩录入 - ${selectedExam?.examType} (${selectedExam ? dayjs(selectedExam.date.iso || selectedExam.date).format('YYYY-MM-DD') : ''})`}
        open={isGradingModalVisible}
        onOk={() => handleSubmitResults({
          selectedExam,
          results,
          setIsModalVisible: setIsGradingModalVisible,
          setLoading,
          onSuccess: fetchExamDates
        })}
        onCancel={() => setIsGradingModalVisible(false)}
        confirmLoading={loading}
        width={800}
      >
        <Tabs
          items={[
            {
              key: 'pending',
              label: '待录入学员',
              children: renderStudentTable('pending')
            },
            {
              key: 'completed',
              label: '已录入学员',
              children: renderStudentTable('completed')
            }
          ]}
        />
      </Modal>

      <Modal
        title={`修改成绩 - ${editingStudent?.name}`}
        open={isEditModalVisible}
        onOk={() => handleEditSubmit({
          selectedExam,
          editingStudent,
          editResult,
          setSelectedExam,
          setIsEditModalVisible,
          setEditingStudent,
          setEditResult,
          setLoading,
          onSuccess: fetchExamDates
        })}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingStudent(null);
          setEditResult(null);
        }}
        confirmLoading={loading}
      >
        <Radio.Group
          value={editResult}
          onChange={e => setEditResult(e.target.value)}
        >
          <Space>
            <Radio.Button value="passed">通过</Radio.Button>
            <Radio.Button value="failed">不通过</Radio.Button>
            <Radio.Button value="absent">缺考</Radio.Button>
          </Space>
        </Radio.Group>
      </Modal>

      <Modal
        title={`学员详情 - ${selectedStudent?.name}`}
        open={studentDetailVisible}
        onCancel={() => {
          setStudentDetailVisible(false);
          setSelectedStudent(null);
        }}
        footer={null}
        width={800}
      >
        {selectedStudent && (
          <StudentDetail
            student={selectedStudent}
            onClose={() => {
              setStudentDetailVisible(false);
              setSelectedStudent(null);
            }}
          />
        )}
      </Modal>
    </>
  );
};

export default ExamHistory; 